import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";

const ShopSection = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const searchQuery = searchParams.get("search");

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/categories/`)
      .then((res) => {
        setCategories(res.data);

        // Priority 1: If category parameter exists, use it
        if (categoryParam && categoryParam !== "all") {
          setSelectedCategoryId(parseInt(categoryParam));
        }
        // Priority 2: If search query exists, find matching category
        else if (searchQuery) {
          const matchingCategory = res.data.find((cat) =>
            cat.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
          if (matchingCategory) {
            setSelectedCategoryId(matchingCategory.id);
          } else {
            // If no matching category, select first one
            setSelectedCategoryId(res.data.length > 0 ? res.data[0].id : null);
          }
        }
        // Priority 3: Default to first category
        else {
          setSelectedCategoryId(res.data.length > 0 ? res.data[0].id : null);
        }
      })
      .catch(() => setCategories([]));
  }, [categoryParam, searchQuery]);

  const selectedCategory = categories.find(
    (cat) => cat.id === selectedCategoryId
  );

  // Filter materials based on search query
  const filterMaterials = (materials) => {
    if (!searchQuery) return materials;

    // Check if search matches category name
    const matchingCategory = categories.find((cat) =>
      cat.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // If category matches search, show all materials
    if (matchingCategory && matchingCategory.id === selectedCategory?.id) {
      return materials;
    }

    // Otherwise, filter by material name
    return materials.filter((material) =>
      material.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Filter product types based on search query
  const filterProductTypes = (productTypes) => {
    if (!searchQuery) return productTypes;

    // Check if search matches category name
    const matchingCategory = categories.find((cat) =>
      cat.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // If category matches search, show all product types
    if (matchingCategory && matchingCategory.id === selectedCategory?.id) {
      return productTypes;
    }

    // Otherwise, filter by product type name or material name
    return productTypes.filter((pt) => {
      const nameMatch = pt.name.toLowerCase().includes(searchQuery.toLowerCase());
      const materialMatch = (pt.materials || []).some((material) =>
        material.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return nameMatch || materialMatch;
    });
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <section className="shop py-40">
      <div className="container container-lg">
        {/* Sidebar Toggle Button (Mobile Only) */}
        <button
          className="shop-sidebar-toggle d-lg-none"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label="Show categories"
        >
          <i className="ph ph-list"></i>
        </button>

        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="shop-sidebar-backdrop d-lg-none"
            onClick={closeSidebar}
          ></div>
        )}

        <div className="row">
          {/* Sidebar - Category List - INTERMEDIATE SIZE */}
          <div className={`col-lg-3`}>
            <aside
              className={`shop-sidebar ${sidebarOpen ? "active" : ""}`}
              tabIndex={-1}
            >
              <div className="shop-sidebarbox border border-gray-100 rounded-8 p-24 mb-24">
                <h6 className="text-lg border-bottom border-gray-100 pb-16 mb-16" style={{ fontWeight: "600" }}>
                  Product Category
                </h6>
                <ul className="max-h-520 overflow-y-auto scroll-sm">
                  {categories.map((category) => {
                    const isSelected = selectedCategoryId === category.id;
                    return (
                      <li
                        key={category.id}
                        className={`category-item ${isSelected ? "active" : ""}`}
                        style={{
                          cursor: "pointer",
                          padding: isSelected ? "8px 12px" : "6px 0px",
                          borderRadius: isSelected ? "6px" : "0px",
                          backgroundColor: isSelected ? "#f0f0f0" : "transparent",
                          transition: "all 0.2s ease",
                          marginBottom: "8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                        onClick={() => {
                          setSelectedCategoryId(category.id);
                          setSidebarOpen(false);
                        }}
                      >
                        <span 
                          style={{
                            fontWeight: isSelected ? "700" : "500",
                            color: isSelected ? "#000" : "#666",
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          {isSelected && (
                            <i 
                              className="ph ph-check-circle-fill" 
                              style={{ 
                                color: "#00a651", 
                                fontSize: "15px",
                                flexShrink: 0
                              }}
                            />
                          )}
                          {category.name}
                        </span>
                        <span 
                          style={{
                            fontWeight: isSelected ? "600" : "400",
                            color: isSelected ? "#666" : "#999",
                            fontSize: "13px",
                            marginLeft: "8px",
                            flexShrink: 0
                          }}
                        >
                          ({category.product_types ? category.product_types.length : 0})
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </aside>
          </div>

          {/* Main - Product Type & Material Grid */}
          <div className="col-lg-9">
            {/* Search Results Header */}
            {searchQuery && (
              <div className="mb-20 search-header p-20 bg-light rounded-12 border border-gray-200">
                <h5 className="mb-8" style={{ fontSize: "15px", fontWeight: "700" }}>
                  Search results for: <span className="search-term fw-bold">{searchQuery}</span>
                </h5>
                {selectedCategory && (
                  <p className="text-gray-600 mb-0" style={{ fontSize: "13px" }}>
                    Category: <strong>{selectedCategory.name}</strong>
                  </p>
                )}
              </div>
            )}

            {/* Category Filter Header */}
            {categoryParam && !searchQuery && selectedCategory && (
              <div className="mb-20 category-header p-20 bg-light rounded-12 border border-gray-200">
                <h5 className="mb-0" style={{ fontWeight: "700", fontSize: "15px" }}>
                  <i className="ph ph-stack me-8"></i>
                  <strong>{selectedCategory.name}</strong>
                </h5>
              </div>
            )}

            {!selectedCategory ||
            !selectedCategory.product_types ||
            selectedCategory.product_types.length === 0 ? (
              <div className="text-center py-40">
                <i className="ph ph-package text-8xl text-gray-300 mb-16 d-block"></i>
                <p className="text-xl text-gray-600">
                  No product types found for this category.
                </p>
              </div>
            ) : (
              <div className="product-types-list">
                {filterProductTypes(selectedCategory.product_types).length === 0 ? (
                  <div className="text-center py-40">
                    <i className="ph ph-magnifying-glass text-8xl text-gray-300 mb-16 d-block"></i>
                    <p className="text-xl text-gray-600">
                      No products found matching "{searchQuery}"
                    </p>
                  </div>
                ) : (
                  filterProductTypes(selectedCategory.product_types).map((productType) => {
                    const filteredMaterials = filterMaterials(
                      productType.materials || []
                    );

                    if (searchQuery && filteredMaterials.length === 0)
                      return null;

                    return (
                      <div
                        key={productType.id}
                        className="product-type-card border rounded-16 p-28 mb-40 bg-white"
                      >
                        <h5 className="product-type-title text-xl fw-bold mb-24 d-flex align-items-center">
                          <i className="ph ph-package me-8" /> {productType.name}
                          {productType.is_custom_size && (
                            <span className="badge bg-main-600 ms-auto" style={{ fontSize: '12px' }}>
                              Custom Size Available
                            </span>
                          )}
                        </h5>
                        {filteredMaterials.length > 0 ? (
                          <div className="row gy-4">
                            {filteredMaterials.map((material) => (
                              <div key={material.id} className="col-6 col-md-4">
                                <div className="material-card border rounded-12 p-18 h-100 d-flex flex-column align-items-center transition-all hover-shadow">
                                  <div className="material-img-box mb-14">
                                    <img
                                      src={
                                        material.image_swatch ||
                                        material.swatch_url ||
                                        "assets/images/thumbs/default-material.png"
                                      }
                                      alt={material.name}
                                      style={{
                                        width: 80,
                                        height: 80,
                                        objectFit: "cover",
                                        borderRadius: 10,
                                        border: "1.5px solid #e5e7eb",
                                        background: "#fff",
                                      }}
                                      onError={(e) => {
                                        e.target.src =
                                          "assets/images/thumbs/default-material.png";
                                      }}
                                    />
                                  </div>
                                  <div className="material-info text-center flex-grow-1 w-100">
                                    <h6 className="material-name fw-semibold mb-2">
                                      {material.name}
                                    </h6>
                                    {material.material_cost_per_sqft && (
                                      <div className="mb-2 text-sm text-gray-500">
                                        ${material.material_cost_per_sqft} / sqft
                                      </div>
                                    )}
                                  </div>
                                  <Link
                                    to={`/product-details-material?type=${productType.id}&material=${material.id}`}
                                    className="btn btn-main btn-lg mt-10 w-100"
                                  >
                                    Customize now{" "}
                                    <i className="ph ph-shopping-cart ms-6" />
                                  </Link>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-materials-message text-muted mb-16">
                            <i className="ph ph-package-x me-8"></i>
                            No materials available for this product type.
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShopSection;
